import React, { FC, useCallback, useMemo, useState } from 'react';

import {
	BUTTON_TYPES,
	EvBodyMedium,
	EvFlexColumn,
	EvFlexRow,
	EvHeadingSmall,
	TEXT_COLOR,
	TITLE_MODES
} from '@evinced-private/ui-common';

import { EvDivider } from 'src/common/components/ev-divider';
import { PlatformPopup } from 'src/common/components/platform-popup/PlatformPopup';
import { Logger } from 'src/common/services/Logger';

import { Label, LabelType, LabelValue, PanelMode } from '../../types';

import { LabelDetailsSection } from './sections/label-details';
import { LABEL_DETAILS_LABELS } from './sections/label-details/LabelDetailsSection.labels';
import { LabelValuesSection } from './sections/label-values';
import { LABEL_VALUES_LABELS } from './sections/label-values/LabelValuesSection.labels';
import { LABEL_PANEL_WIDTH } from './LabelPanel.constants';
import { LABEL_PANEL_LABELS } from './LabelPanel.labels';
import { LabelPanelProps } from './LabelPanel.types';
import { validateDescription, validateDisplayName, validateNewValue } from './LabelPanel.utils';

const createEmptyLabel = (): Label => ({
	key: '',
	displayName: '',
	description: '',
	type: LabelType.CUSTOM,
	values: []
});

export const LabelPanel: FC<LabelPanelProps> = ({
	activeLabel,
	panelMode,
	productId,
	isSaving,
	onSave,
	onClose
}) => {
	const [editedLabel, setEditedLabel] = useState<Label>(() =>
		activeLabel ? { ...activeLabel } : createEmptyLabel()
	);
	const [displayNameError, setDisplayNameError] = useState<string | undefined>();
	const [descriptionError, setDescriptionError] = useState<string | undefined>();

	const isCoreLabel = editedLabel.type === LabelType.CORE;

	const isValid = !displayNameError && !descriptionError;
	const hasRequiredFields = !!editedLabel.displayName.trim();

	const canSave = useMemo(() => isValid && hasRequiredFields, [isValid, hasRequiredFields]);

	const handleDisplayNameChange = useCallback((value: string) => {
		setEditedLabel((prev) => ({ ...prev, displayName: value }));
		setDisplayNameError(validateDisplayName(value));
	}, []);

	const handleDescriptionChange = useCallback((value: string) => {
		setEditedLabel((prev) => ({ ...prev, description: value }));
		setDescriptionError(validateDescription(value));
	}, []);

	const handleAddValue = useCallback(
		(value: string) => {
			const error = validateNewValue(value, editedLabel.values);
			if (error) {
				return error;
			}
			const newValue: LabelValue = { value: value.trim(), dashboardVisible: true };
			setEditedLabel((prev) => ({ ...prev, values: [...prev.values, newValue] }));
			return undefined;
		},
		[editedLabel?.values]
	);

	const handleRemoveValue = useCallback((value: string) => {
		setEditedLabel((prev) => ({
			...prev,
			values: prev.values.filter((v) => v.value !== value)
		}));
	}, []);

	const handleToggleDashboardVisibility = useCallback((value: string) => {
		setEditedLabel((prev) => ({
			...prev,
			values: prev.values.map((v) =>
				v.value === value ? { ...v, dashboardVisible: !(v.dashboardVisible ?? true) } : v
			)
		}));
	}, []);

	const handleSave = useCallback(async () => {
		try {
			await onSave(productId, editedLabel);
		} catch (error) {
			Logger.error('LabelPanel -> handleSave failed:', error);
		}
	}, [productId, onSave, editedLabel]);

	const { displayName, description } = editedLabel;

	const title = useMemo(
		() =>
			panelMode === PanelMode.EDIT
				? LABEL_PANEL_LABELS.TITLE_EDIT(displayName, isCoreLabel)
				: LABEL_PANEL_LABELS.TITLE_ADD,
		[panelMode, displayName, isCoreLabel]
	);

	return (
		<PlatformPopup
			popupWidth={LABEL_PANEL_WIDTH}
			isControlled
			isBgTransparent
			fullHeight
			horizontalPosition="right"
			titleMode={TITLE_MODES.NORMAL}
			title={title}
			isOpen
			onClose={onClose}
			isPopupLoading={isSaving}
			buttons={[
				{
					onClick: onClose,
					title: LABEL_PANEL_LABELS.CANCEL,
					type: BUTTON_TYPES.GHOST
				},
				{
					onClick: handleSave,
					title: LABEL_PANEL_LABELS.SAVE,
					type: BUTTON_TYPES.PRIMARY,
					disabled: !canSave || isSaving
				}
			]}
		>
			{panelMode === PanelMode.EDIT && (
				<EvBodyMedium color={TEXT_COLOR.CONTENT_3}>{LABEL_PANEL_LABELS.SUBTITLE_EDIT}</EvBodyMedium>
			)}
			<EvFlexRow padding={['NONE', 'NONE', 'MEDIUM', 'NONE']}>
				<EvDivider />
			</EvFlexRow>
			<EvFlexColumn gap="XLARGE">
				<EvFlexColumn gap="LARGE">
					<EvHeadingSmall as="h3" color={TEXT_COLOR.CONTENT_2}>
						{LABEL_DETAILS_LABELS.SECTION_TITLE}
					</EvHeadingSmall>
					<LabelDetailsSection
						displayName={displayName}
						description={description ?? ''}
						onDisplayNameChange={handleDisplayNameChange}
						onDescriptionChange={handleDescriptionChange}
						displayNameError={displayNameError}
						descriptionError={descriptionError}
					/>
				</EvFlexColumn>
				<EvFlexRow>
					<EvDivider />
				</EvFlexRow>
				<EvFlexColumn gap="LARGE">
					<EvHeadingSmall as="h3" color={TEXT_COLOR.CONTENT_2}>
						{LABEL_VALUES_LABELS.SECTION_TITLE}
					</EvHeadingSmall>
					<LabelValuesSection
						values={editedLabel.values}
						onAddValue={handleAddValue}
						onRemoveValue={handleRemoveValue}
						onToggleDashboardVisibility={handleToggleDashboardVisibility}
					/>
				</EvFlexColumn>
			</EvFlexColumn>
		</PlatformPopup>
	);
};
